// dependancys
import React, { useEffect, useState, useGlobal } from "reactn";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";

// components
import { ProfileInfoContainer, BusinessBtnWrapper, BusinessDescWrapper } from "./ProfileInfo.Styled";
import { InfoContainer, InfoContainerLocked } from "../../../../_common/containers/containers.styled";
import Icon from "../../../../_common/icons/ReturnIcons";
import Error from "../../../../_common/error/Error";
import Security from "../_security/Security";

// hooks
import ProfileHooks from "../../../../../hooks/profileHooks";
import AddressHooks from "../../../../../hooks/addressHooks";

const ProfileInfo = ({ profile }) => {
    const [profileInfo, setProfileInfo] = useState({});
    const [phoneNumbers, setPhoneNumbers] = useState(null);
    const [defaultAddress, setDefaultAddress] = useState(null);
    const [businessAddress, setBusinessAddress] = useState(null);
    const [business, setBusiness] = useState(null);
    const [editFlow, setEditFlow] = useState(true);
    const [isBusiness, setIsBusiness] = useGlobal("isBusiness");

    const { getDefaultAddress, formatAddress, setProfileValues, handleMultipleAddress, values, moneyUser } = ProfileHooks();
    const { updateAddressObject, setAdditionalAddressVals } = AddressHooks();
    const history = useHistory();

    const anyProfileInfo = () => {
        if ((profileInfo.firstName && profileInfo.lastName) || profileInfo.phoneNumbers || defaultAddress) {
            return true;
        }
        return false;
    };

    const anyBusinessInfoInfo = () => {
        if (profileInfo.business && profileInfo.business.name) {
            return true;
        }
        return false;
    };

    useEffect(() => {
        if (profile.profile) {
            const profileTemp = profile.profile;
            const defAddress = getDefaultAddress(profile.addresses, "Home");
            const defBusinessAddress = getDefaultAddress(profile.addresses, "Business");
            const businessCountry = defBusinessAddress ? defBusinessAddress.country : "";
            const personalCountry = defAddress ? defAddress.country : "";

            // set profile, phone and business information
            setProfileInfo(profileTemp);
            setPhoneNumbers(profile.phones);
            setBusiness(profileTemp.business);

            // Get default business and home addresses
            if (defAddress) {
                setDefaultAddress(formatAddress(defAddress));
                updateAddressObject("profileAddress", defAddress);
            }

            if (defBusinessAddress) {
                setBusinessAddress(formatAddress(defBusinessAddress));
                updateAddressObject("businessAddress", defBusinessAddress);
            }

            // set profile vals (and returns the updated global values object)
            let updatedValues = setProfileValues(profile, personalCountry, businessCountry);

            // set the additional values, e.g. manual and international fields (and returns the updated global values object)
            updatedValues = setAdditionalAddressVals("profileAddress", defAddress || {}, updatedValues);

            // set the additional business address values (e.g manual and international)
            setAdditionalAddressVals("businessAddress", defBusinessAddress || {}, updatedValues);

            // If there is an address returned from the API
            const allAddresses = {};
            if (defAddress) {
                allAddresses.profileAddress = defAddress;
            }
            if (defBusinessAddress) {
                allAddresses.businessAddress = defBusinessAddress;
            }

            // set business and profile addresses
            handleMultipleAddress(allAddresses);
        }
    }, [profile]);

    return (
            <ProfileInfoContainer>
                {profile.email && (
                    <InfoContainer>
                        <h4 className="h6">Email</h4>
                        <p className="profile__name">
                            {profile.email} {values.userIsVerified && <span className="verified">Verified user</span>}
                        </p>
                        <p className="profile__note">Your email address is your username and cannot be changed</p>
                    </InfoContainer>
                )}
                <Security />
                <InfoContainer>
                    <h4>Your personal information</h4>
                    {!anyProfileInfo() && (
                        <Error
                            errorHeading="Information required"
                            errorText="Please update your personal information in order to complete your profile."
                            errorLinkText="Update your profile"
                            errorLink="/Profile/edit-profile"
                        ></Error>
                    )}
                    {profileInfo.firstName && profileInfo.lastName && (
                        <>
                            <h5 className="h6">Name</h5>
                            <p>
                                {profileInfo.firstName} {profileInfo.lastName}
                            </p>
                        </>
                    )}
                    {phoneNumbers &&
                        phoneNumbers.map((number, i) => {
                            return (
                                <React.Fragment key={i}>
                                    <h5 className="h6">{number.type} phone number</h5>
                                    <p>{number.number}</p>
                                </React.Fragment>
                            );
                        })}
                    {defaultAddress && (
                        <>
                            <h5 className="h6">Address</h5>
                            <p>{defaultAddress}</p>
                        </>
                    )}
                    {anyProfileInfo() && !moneyUser && (
                        <button
                            type="button"
                            className="infoContainer__edit"
                            onClick={() => {
                                history.push("/Profile/edit-profile");
                            }}
                            aria-label="Edit Personal Information"
                        >
                            <span className="edit__text">Edit</span> <Icon src="editIcon" />
                        </button>
                    )}
                    {moneyUser && <InfoContainerLocked type="button" aria-label="You do not have access to edit this information" aria-disabled="true" />}
                </InfoContainer>

                {!anyBusinessInfoInfo() && (
                    <InfoContainer secondary={!anyBusinessInfoInfo()}>
                        <>
                            <div className="flex--space-between">
                                <BusinessDescWrapper>
                                    <h4>Are you a business?</h4>
                                    <p>Add your business to your profile to access business services and discounts.</p>
                                </BusinessDescWrapper>
                                <BusinessBtnWrapper>
                                    <button
                                        type="button"
                                        className="gtm-banner bn bn--primary bn--primary-c blueBtn"
                                        onClick={() => {
                                            history.push("/Profile/edit-business");
                                        }}
                                    >
                                        Add your business
                                    </button>
                                </BusinessBtnWrapper>
                            </div>
                        </>
                    </InfoContainer>
                )}
                {anyBusinessInfoInfo() && (
                    <InfoContainer secondary={!anyBusinessInfoInfo()}>
                            <>
                                <h4>Your business</h4>
                                <p>For business services, we'll use your business information by default.</p>
                                {business.name && (
                                    <>
                                        <h5 className="h6">Company name</h5>
                                        <p>{business.name}</p>
                                    </>
                                )}
                                {businessAddress && (
                                    <>
                                        <h5 className="h6">Address</h5>
                                        <p>{businessAddress}</p>
                                    </>
                                )}
                                {business.companyRegNumber && (
                                    <>
                                        <h5 className="h6">Company registration number</h5>
                                        <p>{business.companyRegNumber}</p>
                                    </>
                                )}
                                {business.vatNumber && (
                                    <>
                                        <h5 className="h6">VAT number</h5>
                                        <p>{business.vatNumber}</p>
                                    </>
                                )}
                                {business.industry && (
                                    <>
                                        <h5 className="h6">Industry</h5>
                                        <p>{business.industry}</p>
                                    </>
                                )}
                                <button
                                    type="button"
                                    className="infoContainer__edit"
                                    onClick={() => {
                                        history.push("/Profile/edit-business");
                                    }}
                                    to="/Profile/edit-business"
                                    aria-label="Edit Business Information"
                                >
                                    <span className="edit__text">Edit</span> <Icon src="editIcon" />
                                </button>
                            </>
                    </InfoContainer>
                )}
            </ProfileInfoContainer>
    );
};

export default ProfileInfo;
ProfileInfo.propTypes = {
    profile: PropTypes.object
};
